(function() {
    'use strict';

    var userInfo = null;
    var userGroups = null;

    /**
     * @ngdoc service
     * @name AuthService
     *
     * @description
     * Service used for obtaining and refreshing access tokens.
     */
    angular.module('GorillaBoy')
    .factory('AuthService', [
        '$rootScope',
        '$routeParams',
        '$http',
        '$window',
        '$q',
        '$timeout',
        '$log',
        '$translate',
        '$location',
        'RegionService',
        'EndpointService',
        'UserService',
        'SessionManager',
        'TokenService',
        'localStorageService',
        'PreferencesService',
        'AppConstants',
        'MessageManager',
        'SignalProvider',
        function ($rootScope, $routeParams, $http, $window, $q, $timeout, $log, $translate, $location, regionService, endpointService, userService, SessionManager, TokenService, localStorageService, PreferencesService, AppConstants, MessageManager, SignalProvider) {
            var ACCESS_TOKEN = 'gb_access_token',

                IDP_REFRESH_TOKEN = "idp_refresh_token",
                WD_FED_REFRESH_TOKEN = "workdocs_federated_refresh_token",

                IDP_ACCESS_TOKEN = "idp_access_token",
                WD_FED_ACCESS_TOKEN = "workdocs_federated_token",

                ACCESS_TOKEN_EXPIRATION = 'gb_access_token_exp',
                REFRESH_TOKEN = 'gb_refresh_token',

                accessTokenExpiration = null,
                refreshTokenTimer = null;

            // AppConstant bindings
            var httpMethods = AppConstants.HttpMethods;

            function formatFailure (data, status, statusText) {
                var
                    serverMessage = data && (data.message || data.Message),
                    message = status + ' ' + statusText;

                if (serverMessage) {
                    message += ' (' + serverMessage + ')';
                }

                return message;
            }

            // Store the access token and the optional refresh token
            function storeAccessToken (data) {
                var orgName = endpointService.organization();

                if (localStorageService.get(AppConstants.AuthType.AUTH_TYPE) === AppConstants.AuthType.AUTH_TYPE_LOGINV2) {
                    localStorageService.set(orgName + '.' + IDP_ACCESS_TOKEN, data.AccessToken);
                    localStorageService.set(orgName + '.' + WD_FED_ACCESS_TOKEN, data.AccessToken);
                }

                localStorageService.set(ACCESS_TOKEN, data.AccessToken);
                localStorageService.set(ACCESS_TOKEN_EXPIRATION, data.ExpirationDateTime);
                accessTokenExpiration = new Date(data.ExpirationDateTime);

                // Refresh token is optional
                if (data.RefreshToken) {
                    $log.debug("Inside storeAccessToken() we do have RefreshToken");
                    localStorageService.set(REFRESH_TOKEN, data.RefreshToken);
                    if (localStorageService.get(AppConstants.AuthType.AUTH_TYPE) === AppConstants.AuthType.AUTH_TYPE_LOGINV2) {
                        localStorageService.set(orgName + '.' + IDP_REFRESH_TOKEN, data.RefreshToken);
                        localStorageService.set(orgName + '.' + WD_FED_REFRESH_TOKEN, data.RefreshToken);
                    }
                } else {
                    $log.debug("Inside storeAccessToken() we do NOT have RefreshToken");
                    clearRefreshToken();
                }
                // broadcast the event for frictionless edit and O365
                $rootScope.$broadcast(SignalProvider.Auth.AccessTokenUpdated, data.AccessToken, data.ExpirationDateTime, userInfo);
            }

            function clearAccessToken () {
                $log.info(new Date().toISOString() + 'Removing access token');
                localStorageService.remove(ACCESS_TOKEN);
                localStorageService.remove(ACCESS_TOKEN_EXPIRATION);
            }

            function getRefreshToken () {
                return localStorageService.get(REFRESH_TOKEN);
            }

            function clearRefreshToken () {
                $log.info(new Date().toISOString() + 'Removing refresh token');
                localStorageService.remove(REFRESH_TOKEN);
            }

             /*
             * @ngdoc method
             * @name AuthService#isAuthenticated
             *
             * @description
             * Checks if there is an unexpired access token and user info has been loaded.
             * (which implies the access token is valid).
             *
             * @returns {boolean} Returns `true` if a valid access token is available.
             */
            function isAuthenticated () {
               return userInfo !== null && getAccessToken() !== null;
            }

            /*
             * @ngdoc method
             * @name AuthService#getUserInfo
             *
             * @description
             * Returns current user information
             *
             * @returns {Promise} Future user object
             *
             */
            function getUserInfo (refresh) {
                var deferred = $q.defer();

                // Only fetch user info if we don't already have it.
                if (userInfo && !refresh) {
                    deferred.resolve(userInfo);
                    return deferred.promise;
                }

                userService.getUser()
                .then (function (result) {
                    updateUserInfo(result.data.User);
                    SessionManager.setPhotoUploadExpiration(userInfo.ProfilePhotoMetadata);
                    deferred.resolve(userInfo);
                }, function (result) {
                    var errorMessage = formatFailure(result.data, result.status, result.statusText);
                    $log.error('Couldn\'t obtain user info: ' + errorMessage);
                    deferred.reject(errorMessage);
                });

                return deferred.promise;
            }

            function updateUserInfo (user) {
                userInfo = user;
                PreferencesService.init();
            }

            /*
             * @ngdoc method
             * @name AuthService#getUserGroups
             *
             * @description
             * Returns current user groups
             *
             * @returns {Promise} Future user object
             *
             */
            function getUserGroups (userId) {
                var deferred = $q.defer();

                // Only fetch user groups if we don't already have them.
                if (userGroups) {
                    deferred.resolve(userGroups);
                    return deferred.promise;
                }

                userService.getUserGroups(userId)
                .then (function (response) {
                    userGroups = response.data;
                    deferred.resolve(userGroups);
                }, function (error) {
                    var errorMessage = formatFailure(error.data, error.status, error.statusText);
                    $log.error('Couldn\'t obtain user groups: ' + errorMessage);
                    deferred.reject(errorMessage);
                })
            }

            /*
             * @ngdoc method
             * @name AuthService#hasRefreshToken
             *
             * @description
             * Returns a value indicating whether a refresh token is available.
             *
             * @returns {boolean} `true` if a refresh token is available; otherwise, `false`.
             */
            function hasRefreshToken () {
                var hasToken = localStorageService.get(REFRESH_TOKEN);
                return !!hasToken;
            }


            function getAccessTokenExpiration () {
                var expirationString;
                if (!accessTokenExpiration) {
                    // Hydrate variable from storage
                    expirationString = localStorageService.get(ACCESS_TOKEN_EXPIRATION);
                    if (expirationString) {
                        accessTokenExpiration = new Date(expirationString);
                    } else {
                        // Default to old date if somehow no expiration is available (forcing a refresh, eventually)
                        accessTokenExpiration = new Date(0);
                    }
                }
                return accessTokenExpiration;
            }

            function getAccessTokenExpirationRawValue () {
                var expirationString;

                expirationString = localStorageService.get(ACCESS_TOKEN_EXPIRATION);

                return expirationString;
            }

            function getAccessToken () {
                var accessToken = localStorageService.get(ACCESS_TOKEN);

                // Simple case: there is no access token available
                if (!accessToken) {
                    return null;
                }

                // If there is one, we can determine whether it is still valid
                if (getAccessTokenExpiration() > new Date()) {
                    // Current access token is still valid
                    return accessToken;
                }

                // Access token is invalid, nuke it.
                clearAccessToken();
                return null;
            }

            /**
             * @ngdoc method
             * @name AuthService#getAccessToken
             *
             * @description
             * Obtains an access token by "exchanging" the authentication code received by the authentication server.
             *
             * @param {string} authenticationCode The authentication code returned by the authentication server.
             * @returns {Promise} Future object with the following set of properties:
             *
             * - **AccessToken** – `{string}` – The access token.
             * - **ExpirationDateTime** – {string} – The ISO8601-formatted date/time at which `AccessToken` will expire.
             * - **RefreshToken** – `{string}` – The token to use when requesting a new access token after expiration.
             * - **User** – `{object}` – An object with information about the user for which the `AccessToken` was obtained
             */
            function obtainAccessToken (authenticationCode) {
                var req, deferred;

                // Sanity check
                if (!authenticationCode || authenticationCode.length === 0) {
                    throw {error: 'No authorization code provided'};
                }

                req = {
                    method: httpMethods.POST,
                    url: endpointService.endpoint() + 'token',
                    data: {
                        Code: authenticationCode
                    },
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                    }
                };

                deferred = $q.defer();
                $http(req).then(
                    function (result) {
                        storeAccessToken(result.data);
                        updateUserInfo(result.data.User);
                        deferred.resolve({user: result.data.User, data: result.data});
                    },
                    function (result) {
                        var errorMessage = formatFailure(result.data, result.status, result.statusText);
                        $log.error('Couldn\'t obtain an access token: ' + errorMessage);
                        deferred.reject(errorMessage);
                    });

                return deferred.promise;
            }

            /**
             * @ngdoc method
             * @name AuthService#refreshAccessToken
             *
             * @description
             * Attempts to refresh an expired access token.
             *
             * @param {boolean} forceRefresh Set to `true` to ignore a currently valid access token and force
             * a refresh from the authorization server. Otherwise, attempts to re-use an existing, valid
             * (time-wise) access token.
             *
             * @returns {Promise} Future object representing the result of refreshing the access token.
             */
            function refreshAccessToken (forceRefresh) {
                var deferred, accessToken, refreshToken, req;
                $log.debug("Inside refreshAccessToken() forceRefresh flag is " + forceRefresh);
                if (typeof forceRefresh === 'undefined') {
                    forceRefresh = false;
                }

                $log.info(new Date().toISOString() + ' [AuthService] Refreshing access token (forced: ' + (!!forceRefresh) + ')');
                deferred = $q.defer();

                var expRawValue = getAccessTokenExpirationRawValue();
                accessToken = getAccessToken();
                if (accessToken && (new Date(expRawValue || 0)) > (new Date() + 10 * 1000) && !forceRefresh) {
                    // If there's a valid access token, we're done
                    deferred.resolve();
                } else {
                    refreshToken = getRefreshToken();
                    req = {
                        method: httpMethods.POST,
                        url: endpointService.endpoint() + 'token/refresh',
                        data: {
                            RefreshToken: refreshToken
                        }
                    };

                    $http(req)
                    .then(function (result) {
                        // Successfully refreshed.
                        storeAccessToken(result.data);

                        deferred.resolve(result);
                    },function (result) {
                        // Couldn't refresh the access token
                        if (result.status >= 400 && result.status < 500) {
                            // Server is rejecting the request so we can assume the refresh token is no good.
                            clearRefreshToken();
                        }
                        var errorMessage = formatFailure(result.data, result.status, result.statusText);
                        deferred.reject(errorMessage);
                    });
                }

                return deferred.promise;
            }

            // Occurs each time the timer ticks to refresh the access token.
            function onAccessTokenRefreshTick () {
                var sessionTimeout = 0;
                if (!hasRefreshToken())
                    return $q.reject('Cannot refresh access token: no refresh token found');

                return refreshAccessToken(true)
                .then(function(result) {
                    $log.info(new Date().toISOString() + ' Successfully refreshed access token.');
                    TokenService.propagateAccessTokenToTokenService();
                    TokenService.propagateAccessTokenToTokenServiceHancom();
                    // Refresh succeeded, reset the timer to original interval (default).
                    cancelTokenRefreshTimer();
                    startTokenRefreshTask();
                    return result;
                }, function (errorMessage) {
                    $log.error('Couldn\'t obtain an access token: ' + errorMessage);

                    // Server refused to renew access token, let any interested party know
                    // that session is going to end in the nea future.
                    sessionTimeout = 3000;
                    $timeout(function() {
                        MessageManager.addErrorMessage($translate.instant('MESSAGES.SESSION_EXPIRED', {counter: sessionTimeout/1000}));
                    }, 0);

                    //refreshing access token failed, possibly due to failed refresh token call, hence redirect the user to login page
                    $timeout(function () {
                        clearAccessToken();
                        var redirectLocation = window.location.hash.replace(/^#\//g, '');
                        $location.url('/login?redirectPath=' + encodeURIComponent(redirectLocation));
                    }, sessionTimeout);

                    //http://stackoverflow.com/questions/18758058/angularjs-promise-rejection-chaining
                    return $q.reject(errorMessage);
                });
            }

            function cancelTokenRefreshTimer () {
                if (refreshTokenTimer) {
                    $timeout.cancel(refreshTokenTimer);
                    refreshTokenTimer = null;
                }
            }

            // caculate the next interval we will use to refresh access token.
            function caculateRefreshTaskInterval () {
                var now = new Date().getTime(),
                    tokenExpirationTime = getAccessTokenExpiration().getTime();

                if (tokenExpirationTime <= now) {
                    return 0;
                }

                return parseInt((tokenExpirationTime - now) * 0.85);
            }

            /*
             * @ngdoc method
             * @name AuthService#startTokenRefreshTask
             *
             * @param {number=} [interval=] The interval, in milliseconds, at which to refresh the access token.
             */
            function startTokenRefreshTask (interval, forceRefresh) {
                // Start a timer to initiate a token refresh after X minutes
                if (typeof interval === 'undefined') {
                    interval = caculateRefreshTaskInterval();
                }

                if (refreshTokenTimer && !forceRefresh) {
                    // Already in progress, we can ignore.
                    return;
                }
                refreshTokenTimer = $timeout(onAccessTokenRefreshTick, interval);
            }

            /*
             * @ngdoc method
             * @name AuthService#logout
             *
             * @returns {Promise} Future object representing the result of loging out.
             */
            function logout () {
                var deferred, req;

                $log.info(new Date().toISOString() + ' [AuthService] Logging out');
                req = {
                    method: httpMethods.POST,
                    url: endpointService.endpoint() + 'logout',
                    data: {}
                };

                const refreshToken = getRefreshToken();
                
                if (refreshToken) {
                    req.data.RefreshToken = refreshToken;
                }

                deferred = $q.defer();
                $http(endpointService.tokenize(req)).then(
                    function (result) {
                        clearAccessToken();
                        clearRefreshToken();
                        deferred.resolve();
                    },
                    function (result) {
                        var errorMessage = formatFailure(result.data, result.status, result.statusText);
                        $log.error('Couldn\'t inform the server to log out: ' + errorMessage);
                        deferred.reject(errorMessage);
                    });

                return deferred.promise;
            }

            // Return public API for AuthService
            return {
                onAccessTokenRefreshTick : onAccessTokenRefreshTick,
                isAuthenticated: isAuthenticated,
                getUserInfo: getUserInfo,
                updateUserInfo: updateUserInfo,
                getUserGroups: getUserGroups,
                obtainAccessToken: obtainAccessToken,
                getAccessToken: getAccessToken,
                getAccessTokenExpirationRawValue: getAccessTokenExpirationRawValue,
                getAccessTokenExpiration: getAccessTokenExpiration,
                hasRefreshToken: hasRefreshToken,
                refreshAccessToken: refreshAccessToken,
                startTokenRefreshTask: startTokenRefreshTask,
                logout: logout
            };
        }
    ])

    /**
     * Docmentation on AngularJS Interceptors at https://docs.angularjs.org/api/ng/service/$http#interceptors
     */
    .factory('AuthRetryInterceptor', [
        '$injector',
        '$q',
        '$rootScope',
        '$log',
        '$location',
        'localStorageService',
        'AppConstants',
        'SignalProvider',
        'MetricEvents',
        function ($injector, $q, $rootScope, $log, $location, localStorageService, AppConstants, SignalProvider, MetricEvents) {
            var ACCESS_TOKEN_EXPIRATION = AppConstants.ACCESS_TOKEN_EXPIRIRATION_TIME_IN_SEC * 1000,
                tokenRefreshPromise,
                tokenRefreshFailed = false,
                EMPTY_AUTH_TOKEN = '1 validation error detected: Value at \'authenticationToken\' failed to satisfy constraint: Member must not be null',
                FAILED_PASSWORD_ANONYMOUS_ACCESS = 'InvalidPasswordException: Document needs a password.';

            function getHostPartOfUrl(endpointUrl) {
                var indexOfSlash;
                var endpointUrlHostPart;

                //Assuming (safely) all communication happens in https. If not the below thing should be 7
                indexOfSlash = endpointUrl.indexOf('/', 8);
                if (indexOfSlash !== -1) {
                    endpointUrlHostPart = endpointUrl.substring(8, indexOfSlash);
                } else {
                    endpointUrlHostPart = endpointUrl.substring(8);
                }
                return endpointUrlHostPart;
            }

            /**
             * This method checks if the call being made is for one of WD APIs that depend on the validity of the gb_access_token value
             */
            function isWorkDocsApi(url) {
                var EndpointService = $injector.get('EndpointService');

                var endpointUrl = EndpointService.endpoint(),
                    wopiService = EndpointService.wopiEndpoint();

                var callUrlHostPart = getHostPartOfUrl(url);
                return getHostPartOfUrl(endpointUrl) === callUrlHostPart ||  (wopiService && getHostPartOfUrl(wopiService) === callUrlHostPart);
            }

            function validateUntimelyAccessTokenExpiry () {
                var expireTimeStr = localStorageService.get('gb_access_token_exp'),
                    expireTimeVal,
                    currentTimeVal;

                if (expireTimeStr) {
                    expireTimeVal = (new Date(expireTimeStr)).valueOf();
                    currentTimeVal = (new Date()).valueOf();
                    return (expireTimeVal - currentTimeVal) < ACCESS_TOKEN_EXPIRATION;
                }
                return false;
            }

            function applyAccessToken(config) {
                var accessToken;
                var EndpointService = $injector.get('EndpointService');
                var wopiService = EndpointService.wopiEndpoint();

                config.headers = config.headers || {};
                accessToken = localStorageService.get('gb_access_token');
                if (accessToken) {
                    if (_.startsWith(config.url, wopiService)) {
                        config.headers.Authentication = accessToken;
                    } else {
                        config.headers.Authentication = 'Bearer ' + accessToken;
                    }
                }

                // This is for anonymous sharing.
                // API expects all anonymous calls don't have authentication attribute in header
                if (config.isAnonymousCall) {
                	delete config.isAnonymousCall;
                	delete config.headers.Authentication;
                }

                return config;
            }

            function isUrlRelatedToTokens(url) {
                var tokenRefreshUrlSuffix = 'api/v1/token/refresh';
                var tokenUrlSuffix = 'api/v1/token';
                return url.lastIndexOf(tokenRefreshUrlSuffix) === url.length - tokenRefreshUrlSuffix.length || 
                    url.lastIndexOf(tokenUrlSuffix) === url.length - tokenUrlSuffix.length;
            }

            return {

                'request': function (config) {
                    if (!isWorkDocsApi(config.url)) {
                        return config;
                    }

                    if (tokenRefreshPromise && !tokenRefreshFailed &&
                        //Check if the URL is for refreshing access token. We don't want that request to be queued up!
                        !isUrlRelatedToTokens(config.url)) {
                        $log.log("Encounterd Token Refresh promise, wrapping config inside it's promise");
                        return tokenRefreshPromise.then(function() {
                            return applyAccessToken(config);
                        }, $q.reject); //http://stackoverflow.com/questions/18758058/angularjs-promise-rejection-chaining
                    }
                    return applyAccessToken(config);
                },
                'response': function(response) {
                    if (response.config && isUrlRelatedToTokens(response.config.url)) {
                        tokenRefreshFailed = false;
                    }
                    return response;
                },
                'responseError': function (response) {
                    // Workaround for circular dependency error if we used Angular's default DI.
                    // Refer to: http://stackoverflow.com/questions/25495942/circular-dependency-found-http-templatefactory-view-state
                    var ClientMetricsService,
                        $http,
                        AuthService;

                    //For syntax errors in JSON response, we don't get response.config object
                    if (!response.config || !isWorkDocsApi(response.config.url)) {
                        return $q.reject(response);
                    }

                    //https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/status
                    if (isUrlRelatedToTokens(response.config.url) && response.status >= 400) {
                        tokenRefreshFailed = true;
                    } else if (!tokenRefreshFailed && (response.status === AppConstants.HttpStatuses.UNAUTHORIZED ||
                        (response.data && response.data.message && response.data.message === EMPTY_AUTH_TOKEN)) &&
                        !(response.data && response.data.Message && response.data.Message === FAILED_PASSWORD_ANONYMOUS_ACCESS)) {

                        ClientMetricsService = $injector.get('ClientMetricsService');
                        $http = $injector.get('$http');
                        AuthService = $injector.get('AuthService');

                        if (!tokenRefreshPromise) {
                            $log.log("Encounterd access token failure scenario, refreshing access token behind the scenes.");
                            ClientMetricsService.addAuthEventToSession(MetricEvents.REFRESH_TOKEN_EVENT.name);
                            if (validateUntimelyAccessTokenExpiry()) {
                                ClientMetricsService.recordCounterEvent(MetricEvents.FORCE_REFRESH_TOKEN_COUNTER, 1);
                            }
                            tokenRefreshPromise = AuthService.onAccessTokenRefreshTick().then(function(response) {
                                tokenRefreshPromise = null;
                                return response;
                            }, function(response) {
                                //https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/status
                                if (!response || !response.status || response.status >= 400) {
                                    tokenRefreshFailed = true;
                                }
                                tokenRefreshPromise = null;
                                return $q.reject(response);
                            });
                        } else {
                            $log.log("Encounterd access token failure scenario, waiting on pending refresh token promise to retry method");
                        }
                        return $q(function(resolve, reject) {
                            tokenRefreshPromise.then(function() {
                                return $http(response.config).then(resolve, reject);
                            }, function() {
                                return reject(response);
                            });
                        });
                    }
                    //If it's not 401 or an error with "401" specific response, then just send it upstream
                    return $q.reject(response);
                }
            };
        }
    ])
    .factory('authenticatedUser', function () {
        function getUserInfo () {
            return userInfo;
        }

        function getUserGroups () {
            return userGroups;
        }

        return {getUserInfo: getUserInfo,
            getUserGroups: getUserGroups,
        };
    });

    //Don't push interceptor here, all interceptors are ordered in the app.module.ts config() callback function

}());
